import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, errorCode, errorMessage, showType, data } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage || '请求失败');
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          // 业务码 Token 过期：清登录态并跳转（拦截器已跳时此处再防一次）
          if (errorCode === 401 || errorCode === 20005) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('permissions');
            const onLoginPage =
              window.location.pathname === '/user/login' ||
              window.location.pathname.endsWith('/user/login');
            if (!onLoginPage) {
              message.error(errorMessage || '登录已过期，请重新登录');
              window.location.href = '/user/login';
            }
            return;
          }
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 从localStorage获取Token并添加到请求头
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    ((response: any) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as {
        code: number;
        message: string;
        data: any;
      };

      // 后端返回格式：{ code, message, data }
      // 转换为Pro期望的格式：{ success, data, errorMessage }
      if (data?.code === 200) {
        return {
          ...response,
          data: {
            success: true,
            data: data.data,
            errorMessage: data.message,
          },
        };
      } else {
        // Token 无效/过期（HTTP 401 或业务码 20005）
        if (data?.code === 401 || data?.code === 20005) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('permissions');
          const onLoginPage =
            window.location.pathname === '/user/login' ||
            window.location.pathname.endsWith('/user/login');
          if (!onLoginPage) {
            window.location.href = '/user/login';
          }
        }
        return {
          ...response,
          data: {
            success: false,
            data: data.data,
            errorMessage: data.message || '请求失败',
            errorCode: data.code,
          },
        };
      }
    }) as any,
  ],
};
